import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { from, Observable, switchMap } from 'rxjs';

import { environment } from '../../../environments/environment';

/** Read by the API, which keeps it on the profile at each sign-in. */
export const APP_VERSION_HEADER = 'x-app-version';

/**
 * Tells our API which version of the application is calling.
 *
 * Only our own API hears it: a header sent to Google or to the CDN would tell
 * a third party something it has no use for. And only from a phone: a browser
 * build has no store version to report.
 *
 * The version is read once. If it cannot be read, the call leaves without the
 * header rather than waiting or failing: the API treats a missing version as
 * "keep the last one known".
 */
@Injectable({ providedIn: 'root' })
export class AppVersionInterceptor implements HttpInterceptor {
  #version?: Promise<string | null>;

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!Capacitor.isNativePlatform() || !request.url.startsWith(environment.api)) {
      return next.handle(request);
    }
    return from(this.version()).pipe(
      switchMap((version) =>
        next.handle(version ? request.clone({ headers: request.headers.set(APP_VERSION_HEADER, version) }) : request),
      ),
    );
  }

  private version(): Promise<string | null> {
    this.#version ??= App.getInfo()
      .then(({ version }) => version || null)
      .catch(() => null);
    return this.#version;
  }
}
